import { useSeo } from '@/lib/seo';
import Header from '@/components/Header';
import { SeverityChip } from '@/components/SeverityChip';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Polyline, Polygon, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ArrowLeft, Clock, MapPin, AlertTriangle, RotateCw, CheckCircle2 } from 'lucide-react';
import dayjs from 'dayjs';

// leaflet default marker fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const svgIcon = (color) => L.divIcon({
  className: 'ptt-marker',
  html: `<div style="width:22px;height:22px;background:${color};border:3px solid #fff;border-radius:50%;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

// Fit the map to the extent of a line event so the whole closure/route is visible.
function FitToLine({ path }) {
  const map = useMap();
  useEffect(() => {
    if (path && path.length) {
      const all = path.flat();
      if (all.length) {
        try { map.fitBounds(L.latLngBounds(all), { padding: [30, 30] }); } catch (e) { /* noop */ }
      }
    }
  }, [path, map]);
  return null;
}

export default function AlertDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/alerts/${id}`).then((r) => setA(r.data)).catch((e) => setErr(e?.response?.data?.detail || 'Not found')).finally(() => setLoading(false));
  }, [id]);

  const seoTitle = a
    ? `${a.event_type || 'Highway alert'}${a.highway ? ` on ${a.highway}` : ''}${a.location_area ? ` — ${a.location_area}` : ''}`.slice(0, 90)
    : 'Highway Alert';
  const seoDesc = a
    ? `${a.severity ? `${a.severity.toUpperCase()} · ` : ''}${a.status || 'Active'} — ${a.event_type || 'highway event'}${a.location_area ? ` at ${a.location_area}` : ''}${a.highway ? ` (${a.highway})` : ''}. Live NZ highway alert from NZTA Waka Kotahi via PTT NZ Alerts.`
    : undefined;
  const seoJsonLd = a
    ? {
        '@context': 'https://schema.org',
        '@type': 'SpecialAnnouncement',
        name: seoTitle,
        text: a.description || seoDesc,
        datePosted: a.created_at || a.updated_at || undefined,
        url: `https://alerts.pttnz.net/alerts/${id}`,
        spatialCoverage: {
          '@type': 'Place',
          name: a.location_area || a.highway || 'New Zealand',
          address: { '@type': 'PostalAddress', addressCountry: 'NZ' },
        },
        publisher: { '@type': 'Organization', name: 'PTT NZ Alerts' },
      }
    : null;
  useSeo({ title: seoTitle, description: seoDesc, path: `/alerts/${id}`, jsonLd: seoJsonLd });

  if (loading) return (<><Header /><div className="max-w-4xl mx-auto p-8">Loading…</div></>);
  if (err) return (<><Header /><div className="max-w-4xl mx-auto p-8"><Link to="/alerts" className="btn btn-ghost mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link><div className="card text-center py-16">{err}</div></div></>);

  const sevColor = a.severity === 'red' ? '#E11D48' : a.severity === 'orange' ? '#F7941D' : '#FBBF24';
  const isAdmin = user?.role === 'admin';
  const isResolved = (a.status || '').toLowerCase().startsWith('resolved');
  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/alerts/${a.id}/status?status=${status}`);
      toast.success(status === 'Resolved' ? 'Alert resolved — the feed won\u2019t revive it' : 'Alert reopened');
      const r = await api.get(`/alerts/${a.id}`);
      setA(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-5xl mx-auto p-4 md:p-8">
        <Link to="/alerts" className="btn btn-ghost mb-6" data-testid="alert-back"><ArrowLeft className="w-4 h-4" /> All alerts</Link>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <div className="card">
              <div className="flex items-start flex-wrap gap-3 mb-4">
                <SeverityChip severity={a.severity} size="lg" />
                {a.highway && <span className="chip chip-outline-orange">{a.highway}{a.island ? ` · ${a.island === 'north' ? 'North' : 'South'}` : ''}</span>}
                {a.cause && a.cause.toLowerCase() !== (a.event_type || '').toLowerCase() && <span className="chip" style={{ background: '#f4f4f5', color: '#52525b' }} data-testid="alert-detail-cause">{a.cause}</span>}
                {a.source === 'manual' && <span className="chip chip-outline-yellow">Manual broadcast</span>}
                <span className="chip" style={{ background: '#f4f4f5', color: '#52525b' }}>{a.status}</span>
              </div>
              <h1 className="font-display font-black text-3xl md:text-4xl mb-2">{a.event_type}</h1>
              <div className="text-lg text-zinc-800 font-medium mb-4"><MapPin className="w-4 h-4 inline mr-1 text-zinc-400" />{a.location_area}</div>
              <p className="text-zinc-700 leading-relaxed whitespace-pre-line">{a.description}</p>

              {a.alternative_route && a.alternative_route !== 'N/A' && a.alternative_route !== 'Not applicable' && (
                <div className="mt-6 p-4 rounded-lg bg-teal-50 border border-teal-200">
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-teal-800 mb-1 flex items-center gap-2"><RotateCw className="w-3.5 h-3.5" /> Alternative Route</div>
                  <div className="text-teal-900 text-sm">{a.alternative_route}</div>
                </div>
              )}
            </div>

            {/* Map */}
            {((a.path && a.path.length) || (a.lat && a.lng)) && (
              <div className="card p-0 overflow-hidden">
                <MapContainer center={[a.lat || -41.2, a.lng || 172.5]} zoom={a.geometry_type === 'polygon' ? 8 : 11} style={{ height: 400, width: '100%' }} data-testid="alert-map">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                  {a.geometry_type === 'polygon' && a.path && a.path.length ? (
                    <>
                      <FitToLine path={a.path} />
                      {a.path.map((ring, i) => (
                        <Polygon key={i} positions={ring} pathOptions={{ color: sevColor, weight: 2, opacity: 0.9, fillColor: sevColor, fillOpacity: 0.18 }} data-testid={`alert-map-poly-${i}`}>
                          <Popup>
                            <div className="text-xs" style={{ minWidth: 180 }}>
                              <div className="font-bold">🌦 {a.event_type}</div>
                              <div>{a.location_area}</div>
                              <div className="text-zinc-500 mt-1">{a.status} · MetService</div>
                            </div>
                          </Popup>
                        </Polygon>
                      ))}
                    </>
                  ) : a.geometry_type === 'line' && a.path && a.path.length ? (
                    <>
                      <FitToLine path={a.path} />
                      {a.path.map((seg, i) => (
                        <Polyline key={i} positions={seg} pathOptions={{ color: sevColor, weight: a.severity === 'red' ? 7 : 5, opacity: 0.9 }} data-testid={`alert-map-line-${i}`}>
                          <Popup>
                            <div className="text-xs" style={{ minWidth: 180 }}>
                              <div className="font-bold">{a.event_type}{a.cause && a.cause.toLowerCase() !== (a.event_type || '').toLowerCase() ? ` · ${a.cause}` : ''}</div>
                              <div>{a.highway ? `${a.highway} · ` : ''}{a.location_area}</div>
                              {a.impact && <div className="mt-1">Impact: {a.impact}</div>}
                              <div className="text-zinc-500 mt-1">{a.status}</div>
                            </div>
                          </Popup>
                        </Polyline>
                      ))}
                    </>
                  ) : (
                    <Marker position={[a.lat, a.lng]} icon={svgIcon(sevColor)}>
                      <Popup>{a.event_type} · {a.location_area}</Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
            )}
          </div>

          <aside className="space-y-6">
            <div className="card">
              <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500 mb-4">Details</div>
              <dl className="space-y-3 text-sm">
                <Row k="Impact" v={a.impact} />
                {a.cause && <Row k="Nature" v={a.cause} />}
                {a.region && <Row k="Region" v={a.region} />}
                <Row k="Planned" v={a.planned ? 'Yes' : 'No'} />
                <Row k="Started" v={a.start_date ? dayjs(a.start_date).format('D MMM, HH:mm') : '—'} />
                <Row k="Ends" v={a.end_date ? dayjs(a.end_date).format('D MMM, HH:mm') : (a.expected_resolution || '—')} />
                <Row k="Source" v={a.source === 'nzta' ? 'NZTA Feed' : a.source === 'metservice' ? 'MetService Weather' : 'Manual (Admin)'} />
                <Row k="Last update" v={a.updated_at ? dayjs(a.updated_at).format('D MMM, HH:mm') : '—'} icon={<Clock className="w-3 h-3" />} />
                {a.external_id && <Row k="Ext. ID" v={a.external_id} />}
              </dl>
              {a.web && (
                <a href={a.web} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:underline" data-testid="alert-web-link">
                  View on MetService →
                </a>
              )}
              {a.landmarks && a.landmarks.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-100">
                  <div className="text-xs tracking-[0.15em] uppercase font-semibold text-zinc-500 mb-2">Nearby</div>
                  <ul className="space-y-1 text-sm text-zinc-700" data-testid="alert-landmarks">
                    {a.landmarks.map((l, i) => (
                      <li key={i} className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 text-zinc-400 mt-0.5 flex-shrink-0" />{l}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {isAdmin && (
              <div className="card" data-testid="admin-alert-controls">
                <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500 mb-3">Admin controls</div>
                <p className="text-sm text-zinc-600 mb-3">Force this event's status. Use this to clear a "stuck" feed event — the poller won't revive a resolved event.</p>
                {!isResolved ? (
                  <button onClick={() => setStatus('Resolved')} className="btn btn-primary w-full" data-testid="admin-force-resolve"><CheckCircle2 className="w-4 h-4" /> Mark resolved</button>
                ) : (
                  <button onClick={() => setStatus('Active')} className="btn btn-ghost w-full" data-testid="admin-force-reopen"><RotateCw className="w-4 h-4" /> Reopen (set active)</button>
                )}
              </div>
            )}

            {a.severity === 'red' && (
              <div className="card border-red-200 bg-red-50">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display font-bold text-red-900">Critical Alert</div>
                    <p className="text-sm text-red-800 mt-1">Road closure or major incident. Avoid this route if possible.</p>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, icon }) {
  return (
    <div className="flex justify-between gap-4 border-b border-zinc-100 pb-2 last:border-0">
      <dt className="text-zinc-500 flex items-center gap-1">{icon}{k}</dt>
      <dd className="text-zinc-900 font-medium text-right">{v || '—'}</dd>
    </div>
  );
}
